// src/pages/sales/PageWorkOrders.jsx
import { useState, useEffect, useRef } from "react";
import * as api from "../../apiService";
import { fmtDate } from "../../utils/dateUtils";
import { useToast } from "../../components/Toast";

const STATUS_STYLE = {
  OPEN:      { bg: "#dbeafe", color: "#1e40af" },
  PARTIAL:   { bg: "#fef3c7", color: "#92400e" },
  COMPLETED: { bg: "#d1fae5", color: "#065f46" },
  CANCELLED: { bg: "#fee2e2", color: "#991b1b" },
};

const PRODUCT_TYPE_LABEL = {
  WATER_BASED: "Water Based",
  HOT_MELT:    "Hot Melt",
  OTHERS:      "Others",
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.OPEN;
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: 4,
      padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
      {status}
    </span>
  );
}

// ── Partial Delivery Modal ─────────────────────────────────────────────────────
function WODeliverModal({ wo, onDeliver, onCancel }) {
  const lines = (wo.lines ?? []).filter(l => l.qtyKg != null && parseFloat(l.qtyKg) > 0);
  const [qtys,   setQtys]   = useState(Object.fromEntries(lines.map(l => [l.id, ""])));
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    const payload = lines
      .filter(l => qtys[l.id] !== "" && parseFloat(qtys[l.id]) > 0)
      .map(l => ({ lineId: l.id, deliveryQty: parseFloat(qtys[l.id]) }));
    if (payload.length === 0) { setError("Enter a delivery quantity for at least one line."); return; }
    setSaving(true); setError(null);
    try {
      await onDeliver(wo.id, { lines: payload });
      onCancel();
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  }

  const fmt = v => (v == null ? "—" : parseFloat(v).toLocaleString(undefined, { maximumFractionDigits: 3 }));

  return (
    <div className="overlay" onClick={onCancel}>
      <div className="modal" style={{ maxWidth: 760, width: "95vw" }}
           onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Record Delivery — {wo.woNumber}</div>
          <button className="btn" onClick={onCancel}>✕</button>
        </div>
        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 14 }}>
          Enter the quantity delivered for each product line. Leave blank to skip a line.
        </div>
        {error && <div className="alert-warn" style={{ marginBottom: 10 }}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="table-wrap" style={{ marginBottom: 16 }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th style={{ textAlign: "right" }}>Ordered (kg)</th>
                  <th style={{ textAlign: "right" }}>Delivered</th>
                  <th style={{ textAlign: "right", width: 130 }}>Deliver Now (kg)</th>
                  <th style={{ textAlign: "right" }}>Balance After</th>
                </tr>
              </thead>
              <tbody>
                {lines.map(l => {
                  const ordered    = parseFloat(l.qtyKg) || 0;
                  const delivered  = parseFloat(l.deliveredQty) || 0;
                  const deliverNow = parseFloat(qtys[l.id]) || 0;
                  const balance    = ordered - delivered - deliverNow;
                  return (
                    <tr key={l.id}>
                      <td className="fw-600">{l.productName}</td>
                      <td style={{ textAlign: "right" }}>{fmt(l.qtyKg)}</td>
                      <td style={{ textAlign: "right", color: delivered > 0 ? "#065f46" : "#9ca3af" }}>
                        {fmt(l.deliveredQty)}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <input
                          type="number" min="0" step="0.001"
                          value={qtys[l.id]}
                          onChange={e => setQtys(q => ({ ...q, [l.id]: e.target.value }))}
                          style={{ width: 110, fontSize: 12, padding: "4px 6px", textAlign: "right",
                            border: "1px solid var(--color-border)", borderRadius: 4 }}
                        />
                      </td>
                      <td style={{ textAlign: "right", fontWeight: 600,
                        color: balance <= 0 ? "#065f46" : balance < ordered ? "#92400e" : "#374151" }}>
                        {balance < 0 ? "Over" : fmt(balance)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button type="button" className="btn" onClick={onCancel}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Record Delivery"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Detail / View Modal ────────────────────────────────────────────────────────
function WODetailModal({ wo, myPerms, onStatusChange, onDelete, onDeliver, onClose }) {
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState(null);
  const [deliverOpen, setDeliverOpen] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading,   setUploading]   = useState(false);
  const [attachError, setAttachError] = useState(null);
  const canManage = myPerms?.includes("CRM_MANAGE");

  useEffect(() => {
    api.fetchWOAttachments(wo.id).then(setAttachments).catch(() => {});
  }, [wo.id]);

  async function changeStatus(status) {
    setSaving(true); setError(null);
    try { await onStatusChange(wo.id, status); }
    catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  async function handleDelete() {
    if (!window.confirm(`Delete sales order ${wo.woNumber}? This cannot be undone.`)) return;
    setSaving(true); setError(null);
    try { await onDelete(wo.id); onClose(); }
    catch (e) { setError(e.message); setSaving(false); }
  }

  async function handleAttachUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setAttachError(null);
    try {
      const meta = await api.uploadWOAttachment(wo.id, file);
      setAttachments(old => [...old, meta]);
    } catch (e) {
      setAttachError(e.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  async function handleAttachDelete(attachId) {
    if (!window.confirm("Remove this attachment?")) return;
    try {
      await api.deleteWOAttachment(wo.id, attachId);
      setAttachments(old => old.filter(a => a.id !== attachId));
    } catch (e) {
      setAttachError(e.message);
    }
  }

  const attachUrl = (attachId) =>
    `${api.BASE_URL}/api/crm/work-orders/${wo.id}/attachments/${attachId}/data`;

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 820, width: "95vw", maxHeight: "90vh", overflowY: "auto" }}
           onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{wo.woNumber}</div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              {fmtDate(wo.date)} &nbsp;·&nbsp; {wo.salesExecutive}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <StatusBadge status={wo.status} />
            <button className="btn" onClick={onClose} style={{ fontSize: 13 }}>✕</button>
          </div>
        </div>

        {error && <div className="alert-warn" style={{ marginBottom: 12 }}>{error}</div>}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px", marginBottom: 20 }}>
          <div><span style={{ fontSize: 11, color: "#6b7280" }}>Customer</span>
            <div style={{ fontWeight: 600 }}>{wo.customerName}</div></div>
          <div><span style={{ fontSize: 11, color: "#6b7280" }}>LPO No.</span>
            <div>{wo.lpoNo || "—"}</div></div>
          <div><span style={{ fontSize: 11, color: "#6b7280" }}>LPO Date</span>
            <div>{wo.lpoDate ? fmtDate(wo.lpoDate) : "—"}</div></div>
          {wo.notes && (
            <div style={{ gridColumn: "1 / -1" }}>
              <span style={{ fontSize: 11, color: "#6b7280" }}>Notes</span>
              <div style={{ fontSize: 13 }}>{wo.notes}</div>
            </div>
          )}
        </div>

        <div className="table-wrap" style={{ marginBottom: 16 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>#</th>
                <th>Type</th>
                <th>Product</th>
                <th>Packaging</th>
                <th style={{ textAlign: "right" }}>Ordered (kg)</th>
                <th style={{ textAlign: "right" }}>Delivered</th>
                <th style={{ textAlign: "right" }}>Balance</th>
                <th>Del. Date</th>
                <th style={{ textAlign: "right" }}>Rate/kg</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {(wo.lines ?? []).map((l, i) => {
                const ordered   = parseFloat(l.qtyKg) || 0;
                const delivered = parseFloat(l.deliveredQty) || 0;
                const balance   = ordered - delivered;
                return (
                  <tr key={l.id}>
                    <td style={{ color: "#9ca3af" }}>{i + 1}</td>
                    <td><span style={{ fontSize: 11, fontWeight: 600, color:
                      l.productType === "WATER_BASED" ? "#1d4ed8" :
                      l.productType === "HOT_MELT"    ? "#7e22ce" : "#374151" }}>
                      {PRODUCT_TYPE_LABEL[l.productType] ?? l.productType}
                    </span></td>
                    <td className="fw-600">{l.productName}</td>
                    <td>{l.packaging || "—"}</td>
                    <td style={{ textAlign: "right" }}>{l.qtyKg ?? "—"}</td>
                    <td style={{ textAlign: "right", color: delivered > 0 ? "#065f46" : "#9ca3af" }}>
                      {delivered > 0 ? delivered.toLocaleString(undefined, { maximumFractionDigits: 3 }) : "—"}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 600,
                      color: balance <= 0 ? "#065f46" : balance < ordered ? "#92400e" : "#374151" }}>
                      {ordered > 0 ? balance.toLocaleString(undefined, { maximumFractionDigits: 3 }) : "—"}
                    </td>
                    <td>{l.deliveryDate ? fmtDate(l.deliveryDate) : "—"}</td>
                    <td style={{ textAlign: "right" }}>{l.ratePerKg ?? "—"}</td>
                    <td style={{ color: "#6b7280" }}>{l.remarks || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Attachments */}
        <div style={{ borderTop: "1px solid var(--color-border)", paddingTop: 14, marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 8 }}>
            Attachments (LPO / Purchase Order)
          </div>
          {attachError && <div className="alert-warn" style={{ marginBottom: 8, fontSize: 12 }}>{attachError}</div>}
          {attachments.length === 0 && (
            <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 8 }}>No attachments yet.</div>
          )}
          {attachments.map(a => (
            <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <a href={attachUrl(a.id)} target="_blank" rel="noreferrer"
                style={{ fontSize: 12, color: "#1d4ed8", textDecoration: "none", flex: 1 }}>
                {a.mimeType === "application/pdf" ? "📄" : "🖼"} {a.fileName}
              </a>
              <button onClick={() => handleAttachDelete(a.id)}
                style={{ fontSize: 11, padding: "2px 8px", background: "none",
                  border: "1px solid #fca5a5", borderRadius: 4, color: "#991b1b", cursor: "pointer" }}>
                Remove
              </button>
            </div>
          ))}
          <label style={{ display: "inline-block", marginTop: 6, cursor: "pointer" }}>
            <span className="btn" style={{ fontSize: 12, padding: "4px 12px" }}>
              {uploading ? "Uploading…" : "+ Upload"}
            </span>
            <input type="file" accept="image/*,application/pdf" style={{ display: "none" }}
              disabled={uploading} onChange={handleAttachUpload} />
          </label>
          <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 8 }}>PDF or image, max 10 MB</span>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {(wo.status === "OPEN" || wo.status === "PARTIAL") && (
            <>
              <button className="btn btn-primary" onClick={() => changeStatus("COMPLETED")} disabled={saving}>
                Mark Completed
              </button>
              <button className="btn" onClick={() => setDeliverOpen(true)} disabled={saving}
                style={{ color: "#92400e", borderColor: "#fde68a" }}>
                Record Partial Delivery
              </button>
              <button className="btn" onClick={() => changeStatus("CANCELLED")} disabled={saving}
                style={{ color: "#991b1b", borderColor: "#fca5a5" }}>
                Cancel SO
              </button>
            </>
          )}
          {wo.status === "OPEN" && canManage && (
            <button className="btn" onClick={handleDelete} disabled={saving}
              style={{ marginLeft: "auto", color: "#991b1b", borderColor: "#fca5a5" }}>
              Delete
            </button>
          )}
          {(wo.status === "COMPLETED" || wo.status === "CANCELLED") && (
            <button className="btn" onClick={() => changeStatus("OPEN")} disabled={saving}>
              Re-open
            </button>
          )}
        </div>

        {deliverOpen && (
          <WODeliverModal
            wo={wo}
            onDeliver={onDeliver}
            onCancel={() => setDeliverOpen(false)}
          />
        )}
      </div>
    </div>
  );
}

// ── Create Modal ───────────────────────────────────────────────────────────────
const EMPTY_LINE = {
  productType: "WATER_BASED", productName: "", packaging: "",
  qtyKg: "", deliveryDate: "", ratePerKg: "", remarks: "",
};

function CustomerSearchInput({ options, value, onChange }) {
  const [query, setQuery] = useState(value || "");
  const [open,  setOpen]  = useState(false);
  const ref = useRef(null);

  // Sync display when value is cleared externally (e.g. SP/BU change)
  useEffect(() => { if (!value) setQuery(""); }, [value]);

  const matches = query.trim().length === 0
    ? options
    : options.filter(o => o.toLowerCase().includes(query.toLowerCase()));

  function pick(name) { onChange(name); setQuery(name); setOpen(false); }

  function handleInput(e) { setQuery(e.target.value); onChange(""); setOpen(true); }

  function handleBlur() {
    setTimeout(() => {
      setOpen(false);
      // If typed text doesn't match a valid option, clear selection
      if (!options.includes(query)) { onChange(""); setQuery(""); }
    }, 150);
  }

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <input
        type="text"
        value={query}
        onChange={handleInput}
        onFocus={() => setOpen(true)}
        onBlur={handleBlur}
        placeholder="Search customer…"
        autoComplete="off"
        style={{ width: "100%", fontSize: 13, padding: "6px 8px",
          border: "1px solid var(--color-border)", borderRadius: 4,
          boxSizing: "border-box" }}
      />
      {open && matches.length > 0 && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50,
          background: "#fff", border: "1px solid #d1d5db", borderRadius: 6,
          boxShadow: "0 4px 12px rgba(0,0,0,0.12)", maxHeight: 220,
          overflowY: "auto", marginTop: 2 }}>
          {matches.map(name => (
            <div key={name}
              onMouseDown={() => pick(name)}
              style={{ padding: "7px 12px", fontSize: 13, cursor: "pointer",
                background: name === value ? "#eff6ff" : "transparent" }}
              onMouseEnter={e => e.currentTarget.style.background = "#f3f4f6"}
              onMouseLeave={e => e.currentTarget.style.background = name === value ? "#eff6ff" : "transparent"}>
              {name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WOCreateModal({ fgs, hmGrades, crmCustomers, userEmail, userName, myPerms, onSave, onCancel }) {
  const today      = new Date().toISOString().slice(0, 10);
  const canManage  = myPerms?.includes("CRM_MANAGE");

  // Build salesperson list for admin dropdown
  const spOptions = canManage
    ? (crmCustomers ?? []).map(sp => ({ email: sp.userEmail, name: sp.userName }))
    : [{ email: userEmail, name: userName }];

  const [selectedSp, setSelectedSp] = useState(spOptions[0]?.email ?? userEmail);
  const [businessUnit, setBusinessUnit] = useState("AMI");
  const [form, setForm] = useState({
    date: today, lpoNo: "", lpoDate: "", notes: "",
  });
  const [lines,      setLines]      = useState([{ ...EMPTY_LINE }]);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState(null);
  const [attachment, setAttachment] = useState(null);

  // Derive customer list from selected SP + business unit
  const spData      = (crmCustomers ?? []).find(sp => sp.userEmail === selectedSp);
  const companyData = spData?.companies?.find(c => c.businessUnit === businessUnit);
  const customerOptions = companyData?.customers?.map(c => c.ledgerName) ?? [];

  const [customerName, setCustomerName] = useState("");
  useEffect(() => { setCustomerName(""); }, [selectedSp, businessUnit]);

  // Product lists
  const wbOptions = (fgs ?? []).map(f => f.id);
  const hmOptions = (hmGrades ?? []).filter(g => g.active).map(g => ({ id: g.id, label: g.displayName || g.id }));

  function setF(key, val)   { setForm(f => ({ ...f, [key]: val })); }
  function setLine(i, key, val) {
    setLines(ls => ls.map((l, idx) => {
      if (idx !== i) return l;
      const updated = { ...l, [key]: val };
      // Reset product name when type changes
      if (key === "productType") updated.productName = "";
      return updated;
    }));
  }
  function addLine()     { setLines(ls => [...ls, { ...EMPTY_LINE }]); }
  function removeLine(i) { if (lines.length > 1) setLines(ls => ls.filter((_, idx) => idx !== i)); }

  const spName = spOptions.find(sp => sp.email === selectedSp)?.name ?? userName;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!customerName) { setError("Please select a customer."); return; }
    const invalidLine = lines.find(l => !l.productName.trim());
    if (invalidLine) { setError("All lines must have a product name."); return; }

    setSaving(true); setError(null);
    try {
      const body = {
        businessUnit,
        date:           form.date,
        customerName,
        lpoNo:          form.lpoNo   || null,
        lpoDate:        form.lpoDate || null,
        salesExecutive: spName,
        notes:          form.notes   || null,
        lines: lines.map(l => ({
          productType:  l.productType,
          productName:  l.productName,
          packaging:    l.packaging   || null,
          qtyKg:        l.qtyKg      ? parseFloat(l.qtyKg)      : null,
          deliveryDate: l.deliveryDate || null,
          ratePerKg:    l.ratePerKg   ? parseFloat(l.ratePerKg)  : null,
          remarks:      l.remarks     || null,
        })),
      };
      const created = await onSave(body);
      if (attachment && created?.id) {
        await api.uploadWOAttachment(created.id, attachment);
      }
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  }

  return (
    <div className="overlay" onClick={onCancel}>
      <div className="modal" style={{ maxWidth: 880, width: "95vw", maxHeight: "90vh", overflowY: "auto" }}
           onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>New Sales Order</div>
          <button className="btn" onClick={onCancel}>✕</button>
        </div>

        {error && <div className="alert-warn" style={{ marginBottom: 12 }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* Header fields */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>Company</label>
              <select value={businessUnit} onChange={e => setBusinessUnit(e.target.value)}
                style={{ width: "100%", fontSize: 13, padding: "6px 8px", border: "1px solid var(--color-border)", borderRadius: 4 }}>
                <option value="AMI">AMI (Al Mehraj Industries)</option>
                <option value="AMT">AMT (Al Mehraj Trading)</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>SO Date *</label>
              <input type="date" value={form.date} onChange={e => setF("date", e.target.value)} required
                style={{ width: "100%", fontSize: 13, padding: "6px 8px", border: "1px solid var(--color-border)", borderRadius: 4 }} />
            </div>
            {canManage && (
              <div>
                <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>Sales Executive</label>
                <select value={selectedSp} onChange={e => setSelectedSp(e.target.value)}
                  style={{ width: "100%", fontSize: 13, padding: "6px 8px", border: "1px solid var(--color-border)", borderRadius: 4 }}>
                  {spOptions.map(sp => (
                    <option key={sp.email} value={sp.email}>{sp.name}</option>
                  ))}
                </select>
              </div>
            )}
            {!canManage && (
              <div>
                <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>Sales Executive</label>
                <input type="text" value={spName} readOnly
                  style={{ width: "100%", fontSize: 13, padding: "6px 8px", border: "1px solid var(--color-border)",
                    borderRadius: 4, background: "#f9fafb", color: "#6b7280" }} />
              </div>
            )}
            <div>
              <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>Customer *</label>
              <CustomerSearchInput
                options={customerOptions}
                value={customerName}
                onChange={setCustomerName}
              />
              {customerOptions.length === 0 && (
                <div style={{ fontSize: 11, color: "#b45309", marginTop: 4 }}>
                  No customers assigned for this company
                </div>
              )}
            </div>
            <div>
              <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>LPO No.</label>
              <input type="text" value={form.lpoNo} onChange={e => setF("lpoNo", e.target.value)}
                placeholder="e.g. LPO-2025-001"
                style={{ width: "100%", fontSize: 13, padding: "6px 8px", border: "1px solid var(--color-border)", borderRadius: 4 }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>LPO Date</label>
              <input type="date" value={form.lpoDate} onChange={e => setF("lpoDate", e.target.value)}
                style={{ width: "100%", fontSize: 13, padding: "6px 8px", border: "1px solid var(--color-border)", borderRadius: 4 }} />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>Notes</label>
              <textarea value={form.notes} onChange={e => setF("notes", e.target.value)} rows={2}
                style={{ width: "100%", fontSize: 13, padding: "6px 8px", border: "1px solid var(--color-border)",
                  borderRadius: 4, resize: "vertical", boxSizing: "border-box" }} />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>
                LPO / Purchase Order Attachment
              </label>
              <input type="file" accept="image/*,application/pdf"
                onChange={e => setAttachment(e.target.files?.[0] ?? null)}
                style={{ fontSize: 12 }} />
              {attachment && (
                <span style={{ fontSize: 11, color: "#6b7280", marginLeft: 8 }}>{attachment.name}</span>
              )}
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>PDF or image, max 10 MB (optional)</div>
            </div>
          </div>

          {/* Lines */}
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: "#374151" }}>
            Product Lines
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 12 }}>
            {lines.map((line, i) => (
              <div key={i} style={{ border: "1px solid var(--color-border)", borderRadius: 6, padding: 12 }}>
                <div style={{ display: "grid", gridTemplateColumns: "140px 1fr 110px 90px 110px 90px", gap: 8, marginBottom: 8 }}>
                  {/* Product Type */}
                  <div>
                    <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 3 }}>Type</label>
                    <select value={line.productType} onChange={e => setLine(i, "productType", e.target.value)}
                      style={{ width: "100%", fontSize: 12, padding: "5px 6px", border: "1px solid var(--color-border)", borderRadius: 4 }}>
                      <option value="WATER_BASED">Water Based</option>
                      <option value="HOT_MELT">Hot Melt</option>
                      <option value="OTHERS">Others</option>
                    </select>
                  </div>

                  {/* Product Name */}
                  <div>
                    <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 3 }}>Product *</label>
                    {line.productType === "WATER_BASED" ? (
                      <select value={line.productName} onChange={e => setLine(i, "productName", e.target.value)} required
                        style={{ width: "100%", fontSize: 12, padding: "5px 6px", border: "1px solid var(--color-border)", borderRadius: 4 }}>
                        <option value="">— Select grade —</option>
                        {wbOptions.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    ) : line.productType === "HOT_MELT" ? (
                      <select value={line.productName} onChange={e => setLine(i, "productName", e.target.value)} required
                        style={{ width: "100%", fontSize: 12, padding: "5px 6px", border: "1px solid var(--color-border)", borderRadius: 4 }}>
                        <option value="">— Select grade —</option>
                        {hmOptions.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                      </select>
                    ) : (
                      <input type="text" value={line.productName} onChange={e => setLine(i, "productName", e.target.value)}
                        placeholder="Product / description" required
                        style={{ width: "100%", fontSize: 12, padding: "5px 6px", border: "1px solid var(--color-border)", borderRadius: 4 }} />
                    )}
                  </div>

                  {/* Packaging */}
                  <div>
                    <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 3 }}>Packaging</label>
                    <input type="text" value={line.packaging} onChange={e => setLine(i, "packaging", e.target.value)}
                      placeholder="e.g. 2 DRUMS"
                      style={{ width: "100%", fontSize: 12, padding: "5px 6px", border: "1px solid var(--color-border)", borderRadius: 4 }} />
                  </div>

                  {/* Qty kg */}
                  <div>
                    <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 3 }}>Qty (kg)</label>
                    <input type="number" value={line.qtyKg} onChange={e => setLine(i, "qtyKg", e.target.value)}
                      min="0" step="0.001" placeholder="0"
                      style={{ width: "100%", fontSize: 12, padding: "5px 6px", border: "1px solid var(--color-border)", borderRadius: 4 }} />
                  </div>

                  {/* Delivery Date */}
                  <div>
                    <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 3 }}>Delivery Date</label>
                    <input type="date" value={line.deliveryDate} onChange={e => setLine(i, "deliveryDate", e.target.value)}
                      style={{ width: "100%", fontSize: 12, padding: "5px 6px", border: "1px solid var(--color-border)", borderRadius: 4 }} />
                  </div>

                  {/* Rate/kg */}
                  <div>
                    <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 3 }}>Rate/kg</label>
                    <input type="number" value={line.ratePerKg} onChange={e => setLine(i, "ratePerKg", e.target.value)}
                      min="0" step="0.01" placeholder="0.00"
                      style={{ width: "100%", fontSize: 12, padding: "5px 6px", border: "1px solid var(--color-border)", borderRadius: 4 }} />
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 3 }}>Remarks</label>
                    <input type="text" value={line.remarks} onChange={e => setLine(i, "remarks", e.target.value)}
                      placeholder="Optional"
                      style={{ width: "100%", fontSize: 12, padding: "5px 6px", border: "1px solid var(--color-border)", borderRadius: 4 }} />
                  </div>
                  {lines.length > 1 && (
                    <button type="button" onClick={() => removeLine(i)}
                      style={{ marginTop: 16, padding: "5px 10px", fontSize: 12, background: "none",
                        border: "1px solid #fca5a5", borderRadius: 4, color: "#991b1b", cursor: "pointer" }}>
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <button type="button" className="btn" onClick={addLine}
            style={{ fontSize: 12, marginBottom: 20 }}>
            + Add Line
          </button>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", borderTop: "1px solid var(--color-border)", paddingTop: 16 }}>
            <button type="button" className="btn" onClick={onCancel}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Create Sales Order"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export function PageWorkOrders({ myPerms, userEmail, userName, fgs, hmGrades }) {
  const showToast = useToast();
  const [rows,        setRows]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [createOpen,  setCreateOpen]  = useState(false);
  const [detailWo,    setDetailWo]    = useState(null);
  const [crmCustomers, setCrmCustomers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("OPEN");

  const canCreate = myPerms?.includes("CRM_VIEW");

  function load() {
    setLoading(true);
    api.fetchWorkOrders()
      .then(setRows)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load();
    api.fetchCrmCustomers().then(setCrmCustomers).catch(() => {});
  }, []); // eslint-disable-line

  async function handleCreate(body) {
    const created = await api.createWorkOrder(body);
    setRows(old => [created, ...old]);
    showToast("Sales order created");
    setCreateOpen(false);
    return created;
  }

  async function handleStatusChange(id, status) {
    const updated = await api.updateWorkOrderStatus(id, status);
    setRows(old => old.map(r => r.id === updated.id ? updated : r));
    setDetailWo(updated);
    showToast("Status updated");
  }

  async function handleDeliver(id, body) {
    const updated = await api.deliverWorkOrder(id, body);
    setRows(old => old.map(r => r.id === updated.id ? updated : r));
    setDetailWo(updated);
    showToast("Delivery recorded");
  }

  async function handleDelete(id) {
    await api.deleteWorkOrder(id);
    setRows(old => old.filter(r => r.id !== id));
    showToast("Sales order deleted", "info");
  }

  const canManage = myPerms?.includes("CRM_MANAGE");
  const userRows  = canManage ? rows : rows.filter(r => r.createdBy === userEmail);
  const filtered  = statusFilter === "ALL" ? userRows : userRows.filter(r => r.status === statusFilter);

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Sales Orders</div>
          <div className="page-subtitle">{filtered.length} of {userRows.length} sales order{userRows.length !== 1 ? "s" : ""}</div>
        </div>
        <div className="page-header-right" style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            style={{ fontSize: 12, padding: "5px 10px", border: "1px solid var(--color-border)", borderRadius: 4 }}>
            <option value="OPEN">Open</option>
            <option value="PARTIAL">Partial</option>
            <option value="COMPLETED">Completed</option>
            <option value="CANCELLED">Cancelled</option>
            <option value="ALL">All</option>
          </select>
          {canCreate && (
            <button className="btn btn-primary" onClick={() => setCreateOpen(true)}>+ New Sales Order</button>
          )}
        </div>
      </div>

      {loading && <div style={{ color: "#aaa", fontSize: 12 }}>Loading…</div>}
      {error   && <div className="alert-warn">Error: {error}</div>}

      {!loading && !error && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>SO Number</th>
                <th>Date</th>
                <th>Customer</th>
                <th>Sales Executive</th>
                <th>Products</th>
                <th style={{ textAlign: "right" }}>Total Qty (kg)</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center", color: "#aaa", padding: "32px 0" }}>
                    No sales orders found.
                  </td>
                </tr>
              ) : filtered.map(wo => {
                const lines    = wo.lines ?? [];
                const totalQty = lines.reduce((s, l) => s + (parseFloat(l.qtyKg) || 0), 0);
                const products = lines.map(l => l.productName).join(", ");
                return (
                  <tr key={wo.id} onClick={() => setDetailWo(wo)} style={{ cursor: "pointer" }}>
                    <td className="fw-600" style={{ color: "#1d4ed8" }}>{wo.woNumber}</td>
                    <td>{fmtDate(wo.date)}</td>
                    <td>{wo.customerName}</td>
                    <td style={{ color: "#6b7280" }}>{wo.salesExecutive}</td>
                    <td style={{ fontSize: 12, color: "#374151", maxWidth: 260,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {products || "—"}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {totalQty > 0 ? totalQty.toLocaleString(undefined, { maximumFractionDigits: 3 }) : "—"}
                    </td>
                    <td><StatusBadge status={wo.status} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {createOpen && (
        <WOCreateModal
          fgs={fgs}
          hmGrades={hmGrades}
          crmCustomers={crmCustomers}
          userEmail={userEmail}
          userName={userName}
          myPerms={myPerms}
          onSave={handleCreate}
          onCancel={() => setCreateOpen(false)}
        />
      )}

      {detailWo && (
        <WODetailModal
          wo={detailWo}
          myPerms={myPerms}
          onStatusChange={handleStatusChange}
          onDelete={handleDelete}
          onDeliver={handleDeliver}
          onClose={() => setDetailWo(null)}
        />
      )}
    </>
  );
}
